import React from 'react';
import ImageTools from '../../helpers/ImageTools';
import AnalyticsLogger from '../../helpers/analytics/analyticsLogger';
import BaseComponent from '../../baseClasses/BaseComponent';
import anime from 'animejs';
import MarketComboBox from '../../components/MarketComboBox';
import dataModel from '../../models/DataModel';
import attachmentsIcon from '../../assets/images/attachment-icon-gray.svg';
import tasksIcon from '../../assets/images/my-tasks-icon-gray.svg';
import buildsIcon from '../../assets/images/build-icon-gray.svg';
import cancelIcon from '../../assets/images/cancel-icon-white.svg';
import DataSource from '../../dao/DataSource/DataSource';
import CollectionDataSource from '../../dao/DataSource/CollectionDataSource';
import SelectCompanyComboBox from '../../components/SelectCompanyComboBox';

import './CopyProjectTemplatePopout.scss';
import Input from '../Input';

const project_template = {
  project_template: {
    project_type: {},
    reference_documents: {},
  },
};

class CopyProjectTemplatePopout extends BaseComponent {

  constructor(props) {
    super(props);
    this.state = {
      projectTemplate: props.projectTemplate,
      markets: null,
      selectedMarkets: null,
    };

    this.changeProjectTemplateInfo = this.changeProjectTemplateInfo.bind(this);
    this.closePopup = this.closePopup.bind(this);
    this.handleProjectTemplateCopy = this.handleProjectTemplateCopy.bind(this);
    this.collapsePopup = this.collapsePopup.bind(this);
    this.handleProjectTemplateUpdate = this.handleProjectTemplateUpdate.bind(this);
    this.handleCompanyUpdate = this.handleCompanyUpdate.bind(this);
  }

  handleProjectTemplateUpdate(projectTemplate) {
    this.setState({ projectTemplate });
    if (!this.state.projectTypeName) {
      this.setState({ projectTypeName: projectTemplate.project_type.name + ' (copy)' });
    }
    if (!this.state.projectTemplateName) {
      this.setState({ projectTemplateName: projectTemplate.name + ' (copy)' });
    }
  }

  handleCompanyUpdate(company) {
    if (company) {
      const selectedMarkets = company.markets && company.markets.length ? [{ type: company.markets[0].id, name: company.markets[0].name }]: [];
      this.setState({ company, markets: company.markets, selectedMarkets });
    }
  }

  handleCompaniesUpdate(companies) {
    this.setState({ companies: companies.companies });
  }

  componentDidMount() {
    const currentRole = this.props.currentUser ? this.props.currentUser.role : null;
    this.analyticsLogger = new AnalyticsLogger({
      currentRole,
      isMobile: !!window.cordova,
    });

    this.handleOffScreen();
    this.projectTemplateDataSource = new DataSource('ProjectTemplate', this.props.projectTemplate.id, project_template, this.handleProjectTemplateUpdate);
    this.projectTemplateDataSource.get();
    this.companyDataSource = new DataSource('Company', this.props.currentCompany.id, { company: { markets: {} } }, this.handleCompanyUpdate);
    this.companyDataSource.get();

    if (this.props.currentUser.super_admin) {
      this.companiesCollectionDataSource = new CollectionDataSource('CompaniesCollection', 'Company', { companies: { markets: {} } },
        this.handleCompaniesUpdate, 'headerCompanies', null, null, null,
        this.props.currentCompany.id, 'companies_header');
      this.companiesCollectionDataSource.current();
    }
  }

  componentWillUnmount() {
    if (this.projectTemplateDataSource) {
      this.projectTemplateDataSource.release();
      this.projectTemplateDataSource = null;
    }
    if (this.companyDataSource) {
      this.companyDataSource.release();
      this.companyDataSource = null;
    }
    if (this.companiesCollectionDataSource) {
      this.companiesCollectionDataSource.release();
      this.companiesCollectionDataSource = null;
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.displayCopyPopout != this.props.displayCopyPopout && this.props.displayCopyPopout) {
      this.expandPopup();
    }
  }

  handleOffScreen() {
    const rect = this.copyProjectTemplatePopout.getBoundingClientRect();

    //resets popup to anchor at screen bottom if it overlaps window height
    if ((rect.top + 424) > window.innerHeight) {
      let top = this.copyProjectTemplatePopout.offsetTop + (window.innerHeight - rect.top - 424);
      this.setState({ top });
    }

    //resets popup to anchor at left side of column if it overlaps window width
    const width = this.copyProjectTemplatePopout.offsetWidth;
    if ((rect.left + width) > window.innerWidth) {
      this.setState({ left: -368 });
    }
  }

  expandPopup() {
    return anime({
      targets: this.copyProjectTemplatePopout,
      height: this.props.currentUser.super_admin ? 414 : 364,
      easing: 'easeInOutCubic',
      duration: 200,
    });
  }

  collapsePopup() {
    return anime({
      targets: this.copyProjectTemplatePopout,
      height: 0,
      easing: 'easeInOutCubic',
      duration: 200,
      complete: () => {
        this.closePopup();
      },
    });
  }
  
  getIconForCSType(completionStep) {
    return ImageTools.getGreyedCSIcon(completionStep, 'grey-outline');
  }

  changeProjectTemplateInfo(e) {
    this.setState({ [e.target.name]: e.target.value });
  }

  closePopup() {
    const { handleClosePopup } = this.props;
    if (handleClosePopup) {
      handleClosePopup();
    }
  }

  handleProjectTemplateCopy(e) {
    e.stopPropagation();
    const { projectTemplate, projectTypeName, projectTemplateName, selectedMarkets, copyToCompany } = this.state;
    const marketsToAdd = selectedMarkets.map(market => {
      return{ id: market.type, name: market.name };
    });
    this.collapsePopup();
    dataModel.projectTemplatesDAO.copyProjectTemplate(projectTypeName, projectTemplateName, marketsToAdd, projectTemplate, copyToCompany).then(newProjectTemplate => {
      if (this.props.currentUser.super_admin) {
        dataModel.displayInfoErrorMessage('The Template has been successfully copied.', 4000, 1);
      }
    });
    this.analyticsLogger.logProjectTemplateEvent(projectTemplate, copyToCompany && copyToCompany.id != projectTemplate.company_id ? 'TemplateCopyExternal' : 'TemplateCopyInternal');
  }

  handleMarketChange(markets) {
    this.setState({ selectedMarkets: markets });
  }

  handleCompanySelection(company) {
    if (company) {
      const selectedMarkets = company.markets && company.markets.length ? [{ type: company.markets[0].id, name: company.markets[0].name }] : [];
      this.setState({ copyToCompany: company, markets: company.markets, selectedMarkets });
    }
    else {
      const selectedMarkets = company.markets && company.markets.length ? [{ type: company.markets[0].id, name: company.markets[0].name }]: [];
      this.setState({ copyToCompany: company, markets: this.state.company.markets, selectedMarkets });
    }
  }

  render() {
    const { projectTemplate } = this.state;
    const { projectTypeName, projectTemplateName } = this.state;

    let style = {
      top: this.state.top,
      left: this.state.left,
    };

    let companySelector = null;
    if (this.props.currentUser.super_admin && this.state.companies) {
      const companyOptions = this.state.companies;
  
      companySelector =
      <div className="project-template-info__company-selector">

      <SelectCompanyComboBox 
      id={'select-copy-to-company'} 
      key={'select-copy-to-company'} 
          width={340} 
          height={28}
          hasBorder={false}
          showSearchBar={true}
          searchHint={'Search Companies'}
          noBackground={false}
          adjustHeight={true}
          backgroundIndex={27}
          displayFontStyle={{ fontSize: '12px', fontFamily: 'Open Sans', color: '#000000', lineHeight: '11px', textIndent: '0px', marginTop: '0px' }}
          handleSelection={this.handleCompanySelection}
      currentSelectedCompany={this.state.copyToCompany ? this.state.copyToCompany : this.state.company} companies={companyOptions}
        />
        </div>;

    }

    const versionText = 'Version Number: Version ' + projectTemplate.version;
    let templateInfo = null;
    if (this.state.copyToCompany && this.props.currentCompany.id != this.state.copyToCompany.id) {
      templateInfo = <div className='project-template-info__attachments'>
        <div className='icon-container'>
          <img
            className='builds-count-icon'
            src={buildsIcon}
            alt="builds-count"
          />
          <p>{projectTemplate.builds_count}</p>
        </div>
        <div className='icon-container'>
          <img
            className='tasks-count-icon'
            src={tasksIcon}
            alt="tasks-count"
          />
          <p>{projectTemplate.tasks_count}</p>
        </div>
      </div>;
    }
    else {
      templateInfo = <div className='project-template-info__attachments'>
        <div className='icon-container'>
          <img
            className='builds-count-icon'
            src={buildsIcon}
            alt="builds-count"
          />
          <p>{projectTemplate.builds_count}</p>
        </div>
        <div className='icon-container'>
          <img
            className='tasks-count-icon'
            src={tasksIcon}
            alt="tasks-count"
          />
          <p>{projectTemplate.tasks_count}</p>
        </div>
        <div className='icon-container'>
          <img src={attachmentsIcon} alt="Attachments" />
          <p>{projectTemplate.reference_documents ? projectTemplate.reference_documents.length : 0}</p>
        </div>
      </div>;
    }

    return (
      <article
        className='copy-project-template-popout'
        ref={el => this.copyProjectTemplatePopout = el}
        style={style}
        onClick={e => e.stopPropagation()}
      >
        <header className='copy-project-template-popout__header'>
          COPY TEMPLATE
          <img
            src={cancelIcon}
            alt="cancel"
            className='close-button'
            onClick={this.collapsePopup}
          />
        </header>
        <section className='copy-project-template-popout__container'>
          <div className='project-template-info'>
            {templateInfo}
            <div className='project-template-info__version-info'>{versionText}</div>
            {this.props.currentUser.super_admin && 
              <p className='project-template-info__text'>COMPANY NAME</p>
            }
            {companySelector}
            <p className='project-template-info__text'>PROJECT TYPE NAME</p>
            <Input
              className='project-template-info__input'
              name='projectTypeName'
              value={projectTypeName}
              onChange={this.changeProjectTemplateInfo}
            />
            <p className='project-template-info__text'>TEMPLATE NAME</p>
            <Input
              className='project-template-info__input'
              name='projectTemplateName'
              value={projectTemplateName}
              onChange={this.changeProjectTemplateInfo}
            />
            <p className='project-template-info__text'>MARKETS</p>
            <div className="project-template-info__market-selector">
          <MarketComboBox
            markets={this.state.markets}
            height={32}
            width={340}
            backgroundIndex={27}
            hasBorder={false}
            multiSelect={true}
            defaultText={this.textMap.markets}
            displayFontStyle={{ fontSize: '12px', fontFamily: 'Open Sans', color: '#000000', textIndent: '0px', marginTop: '7px' }}
            handleSelectMarket={this.handleMarketChange}
            currentOption={this.state.selectedMarkets}
              />
              </div>
          </div>
        </section>
        <div className='copy-project-template-popout__button-container'>
          <button className='copy-button' onClick={this.collapsePopup}>
            CANCEL
          </button>
          <button
            className={'copy-button copy-button__copy' + (projectTemplateName && projectTypeName ? '-active' : '')}
            onClick={projectTemplateName && projectTypeName && this.handleProjectTemplateCopy}
          >
            COPY
          </button>
        </div>
      </article>
    );
  }

}

export default CopyProjectTemplatePopout;



// WEBPACK FOOTER //
// ./src/components/CopyProjectTemplatePopout/index.js